<script setup lang="ts">
import { track } from '@/lib/usageTracker'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { formatDistanceToNow } from 'date-fns'
import { useAuthStore } from '@/stores/authStore'
import { postStockAsync } from '@/api/lytAccountingClient'
import { getIntegration } from '@/api/integrations/index.js'
import {
  Activity,
  ChevronDown,
  CreditCard,
  FileText,
  Package,
  RefreshCw,
  Users,
} from 'lucide-vue-next'
import { Alert, Badge, Button, Card, Spinner } from '@/components/ui'
import HealthRail from '@/components/task/HealthRail.vue'
import ActivityFeed from '@/components/task/ActivityFeed.vue'
import { useTaskActivityStore } from '@/stores/taskActivityStore'
import { useDashboardSyncActions } from '@/composables/useDashboardSyncActions'
import {
  invalidateDashboardStatus,
  useDashboardStatusQuery,
} from '@/composables/queries/useDashboardStatusQuery'

const router = useRouter()
const auth = useAuthStore()
const taskActivity = useTaskActivityStore()
const dashboardActions = useDashboardSyncActions()

// Pinia Colada owns the dashboard status read lifecycle (key, cache, loading/error).
const {
  data: dashboard,
  isLoading: statusLoading,
  error: statusError,
  refetch: refetchDashboardStatus,
} = useDashboardStatusQuery()

const syncing = ref<Record<string, boolean>>({})
const reconnecting = ref(false)
/** Write-path errors (sync/reconnect); read errors come from statusError. */
const actionError = ref('')

const loading = computed(() => statusLoading.value)
const error = computed(() => {
  if (actionError.value) return actionError.value
  const err = statusError.value
  if (!err) return ''
  return err instanceof Error ? err.message : String(err) || 'Failed to load dashboard'
})

const entities = ['customers', 'items', 'invoices', 'payments'] as const

const iconMap: Record<string, typeof Users> = {
  customers: Users,
  items: Package,
  invoices: FileText,
  payments: CreditCard,
}

const labelMap: Record<string, string> = {
  customers: 'Customers',
  items: 'Items',
  invoices: 'Invoices',
  payments: 'Payments',
}

const actionLabelMap: Record<string, string> = {
  customers: 'Refresh status',
  items: 'Sync stock',
  invoices: 'Refresh status',
  payments: 'Refresh status',
}

const providerLabel = computed(() => {
  const key = auth.selectedPackageId?.toUpperCase()
  if (key === 'QUICKBOOKS') return 'QuickBooks'
  return auth.selectedPackageId || 'Accounting provider'
})

const healthVariant = computed(() => (dashboard.value?.connected ? 'connected' : 'disconnected'))

const lastSyncRelative = computed(() => {
  if (!mostRecentSync.value) return ''
  return formatDistanceToNow(new Date(mostRecentSync.value), { addSuffix: true })
})

const mostRecentSync = computed(() => {
  const values = Object.values(dashboard.value?.lastSync || {}).filter(Boolean) as string[]
  if (!values.length) return ''
  return values
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((left, right) => right.getTime() - left.getTime())[0]
    ?.toISOString()
})

async function loadDashboard() {
  actionError.value = ''
  await refetchDashboardStatus()
}

async function syncEntity(entity: string) {
  track('btn:Sync' + entity[0].toUpperCase() + entity.slice(1), 'click')
  actionError.value = ''
  syncing.value[entity] = true
  try {
    if (entity === 'items') {
      await postStockAsync({ scope: 'full-sync' })
      await taskActivity.refreshNow({
        includeSummary: true,
        includeRecent: true,
        includeActive: true,
        entityTypes: ['STOCK', 'ITEM'],
        recentLimit: 50,
      })
      // Successful item sync invalidates cached status so counts/lastSync refresh.
      await invalidateDashboardStatus()
    } else {
      await refetchDashboardStatus()
    }
  } catch (e) {
    console.error(`Sync ${entity} failed:`, e)
    actionError.value = (e as Error)?.message || `Unable to refresh ${labelMap[entity].toLowerCase()}`
  } finally {
    syncing.value[entity] = false
  }
}

function reconnectIntegration() {
  track('btn:ReconnectProvider', 'click')
  const providerId = auth.selectedPackageId
  if (!providerId) {
    router.push({ name: 'SetupEntry' })
    return
  }
  const integration = getIntegration(providerId)
  if (!integration) {
    router.push({ name: 'SetupEntry' })
    return
  }
  reconnecting.value = true
  try {
    const url = integration.startOAuth(auth.storeID, auth.invoiceNumber)
    window.location.assign(url)
  } catch (e) {
    console.error('Failed to start OAuth flow', e)
    reconnecting.value = false
    actionError.value = 'Unable to start reconnection. Please try again.'
  }
}

function formatDate(value?: string) {
  if (!value) return 'No sync recorded'
  try {
    return new Intl.DateTimeFormat('en-ZM', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  } catch {
    return String(value)
  }
}

let unsubscribeTaskActivity: (() => void) | null = null

onMounted(() => {
  // Status query auto-fetches on mount via Pinia Colada; only subscribe tasks here.
  unsubscribeTaskActivity = taskActivity.subscribeActivity({
    includeSummary: true,
    includeRecent: true,
    includeActive: true,
    entityTypes: ['STOCK', 'ITEM', 'CUSTOMER', 'INVOICE', 'PAYMENT'],
    recentLimit: 50,
  })
})

onUnmounted(() => {
  unsubscribeTaskActivity?.()
  unsubscribeTaskActivity = null
})
</script>

<template>
  <div class="app-page">
    <section class="app-page-header">
      <div class="app-page-heading">
        <div class="app-page-eyebrow">
          <Activity class="h-4 w-4" />
          <span>Operations board</span>
        </div>
        <h1 class="app-page-title">Connection health and sync operations</h1>
        <p class="app-page-subtitle">
          Check your provider status, confirm company context, and work through sync queues without leaving the dashboard.
        </p>
      </div>
      <div class="app-page-actions">
        <Button variant="secondary" @click="() => { track('btn:RefreshDashboard', 'click'); loadDashboard() }" :loading="loading">
          <RefreshCw class="h-4 w-4" />
          Refresh board
        </Button>
      </div>
    </section>

    <Alert
      :variant="dashboard?.connected ? 'success' : 'warning'"
      compact
      class="dashboard-alert dashboard-health-strip"
    >
      <div class="alert-copy-row">
        <span>
          <strong>{{ providerLabel }}</strong>
          · {{ dashboard?.connected ? 'Connected' : 'Disconnected' }}
          <template v-if="lastSyncRelative"> · Synced {{ lastSyncRelative }}</template>
        </span>
        <Button
          v-if="!dashboard?.connected"
          size="sm"
          @click="() => { track('btn:ReconnectProvider', 'click'); reconnectIntegration() }"
          :loading="reconnecting"
        >
          Reconnect {{ providerLabel }}
        </Button>
        <Button
          v-else
          size="sm"
          variant="secondary"
          @click="() => { track('btn:RefreshDashboard', 'click'); loadDashboard() }"
          :loading="loading"
        >
          Refresh connection
        </Button>
      </div>
    </Alert>

    <Alert v-if="dashboardActions.bulkFeedback.value" variant="info" title="Dashboard bulk sync" compact class="dashboard-alert">
      <div class="alert-copy-row">
        <span>{{ dashboardActions.bulkFeedback.value }}</span>
      </div>
    </Alert>
    <Alert v-if="dashboardActions.retryFeedback.value" variant="info" title="Dashboard task retry" compact class="dashboard-alert">
      <div class="alert-copy-row">
        <span>{{ dashboardActions.retryFeedback.value }}</span>
      </div>
    </Alert>

    <Alert v-if="error" variant="error" title="Sync attention needed" compact class="dashboard-alert">
      <div class="alert-copy-row">
        <span>{{ error }}</span>
        <Button size="sm" variant="outline" @click="() => { track('btn:RefreshDashboard', 'click'); loadDashboard() }">Retry</Button>
      </div>
    </Alert>

    <div v-if="loading && !dashboard" class="loading-panel">
      <Spinner size="lg" />
      <p>Loading current accounting status...</p>
    </div>

    <section class="health-rail-section">
      <h2 class="section-heading">Sync Health</h2>
      <HealthRail :summaries="taskActivity.summaries" />
    </section>
    <section class="dashboard-tiles">
      <!-- DASH-02: Tasks in flight -->
      <Card padding="md" class="tile-card">
        <h2 class="section-heading">Tasks in flight</h2>
        <ul v-if="dashboardActions.inFlightByEntityType.value.length > 0" class="tile-list">
          <li v-for="row in dashboardActions.inFlightByEntityType.value" :key="row.entityType" class="tile-row">
            <router-link :to="`/app/tasks?entityType=${row.entityType}`" class="tile-row-link">
              <Badge variant="default" size="sm">{{ row.count }} {{ row.entityType }}</Badge>
            </router-link>
          </li>
        </ul>
        <div v-else class="tile-empty tile-muted">
          <p>All clear — no tasks in flight</p>
          <router-link to="/app/tasks" class="tile-empty-link">View tasks</router-link>
        </div>
      </Card>

      <!-- DASH-03: Failed in last 24h -->
      <Card padding="md" class="tile-card">
        <router-link to="/app/tasks?status=failed" class="tile-link">
          <h2 class="section-heading">Failed in last 24h</h2>
          <div v-if="dashboardActions.failedTotal.value > 0 || dashboardActions.failedInLast24h.value > 0" class="tile-failed">
            <span class="tile-failed-number">{{ dashboardActions.failedTotal.value }}</span>
            <span class="tile-failed-subtitle">{{ dashboardActions.failedInLast24h.value }} in last 24h</span>
          </div>
          <div v-else class="tile-empty tile-muted">
            <p>No failed tasks</p>
          </div>
        </router-link>
      </Card>
    </section>

    <section class="activity-section">
      <h2 class="section-heading">Recent Activity</h2>
      <ActivityFeed
        :tasks="taskActivity.recentTasks"
        :on-retry="dashboardActions.retryTask"
        :retrying-task-id="dashboardActions.retryingTaskId.value"
      />
    </section>

    <section class="operations-grid">
      <Card v-for="entity in entities" :key="entity" padding="md" class="operation-card">
        <div class="operation-heading">
          <span class="operation-icon">
            <component :is="iconMap[entity]" class="h-4 w-4" />
          </span>
          <div>
            <div class="operation-label">{{ labelMap[entity] }}</div>
            <div class="operation-meta">Queue status and latest sync marker</div>
          </div>
        </div>

        <div class="operation-count">{{ dashboard?.counts?.[entity] ?? 0 }}</div>
        <div class="operation-sync">Last sync: {{ formatDate(dashboard?.lastSync?.[entity]) }}</div>

        <template v-if="entity === 'items'">
          <div class="split-button">
            <Button
              variant="secondary"
              :loading="dashboardActions.bulkSyncing.value"
              @click="() => { track('btn:BulkSyncItems', 'click'); dashboardActions.bulkSyncEntity('items') }"
            >
              <RefreshCw class="h-4 w-4" />
              {{ dashboardActions.bulkSyncing.value ? 'Working…' : 'Sync all unlinked' }}
            </Button>
            <button
              class="split-chevron"
              :aria-label="'Choose entity to sync'"
              @click="dashboardActions.bulkDropdownOpen.value = !dashboardActions.bulkDropdownOpen.value"
            >
              <ChevronDown class="h-4 w-4" />
            </button>
            <ul v-if="dashboardActions.bulkDropdownOpen.value" class="split-menu" role="menu">
              <li role="menuitem" @click="() => { track('btn:BulkSyncStock', 'click'); dashboardActions.bulkSyncEntity('items'); dashboardActions.bulkDropdownOpen.value = false }">Stock</li>
              <li role="menuitem" @click="() => { track('btn:BulkSyncInvoicesMenu', 'click'); dashboardActions.bulkSyncEntity('invoices'); dashboardActions.bulkDropdownOpen.value = false }">Invoices</li>
              <li role="menuitem" @click="() => { track('btn:BulkSyncPaymentsMenu', 'click'); dashboardActions.bulkSyncEntity('payments'); dashboardActions.bulkDropdownOpen.value = false }">Payments</li>
            </ul>
          </div>
        </template>
        <Button
          v-else
          size="sm"
          variant="secondary"
          :loading="syncing[entity]"
          @click="syncEntity(entity)"
        >
          <RefreshCw class="h-4 w-4" />
          {{ syncing[entity] ? 'Working…' : actionLabelMap[entity] }}
        </Button>
      </Card>
    </section>
  </div>
</template>

<style scoped>
.dashboard-alert {
  margin-top: 0.75rem;
}

.alert-copy-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.loading-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 0.75rem;
  color: var(--color-ink-soft);
}

.health-rail-section,
.activity-section {
  margin-top: 0.9rem;
}

.section-heading {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-ink);
  margin-bottom: 0.75rem;
}

.operations-grid {
  margin-top: 0.9rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.operation-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.operation-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.operation-icon {
  width: 1.65rem;
  height: 1.65rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  border: 1px solid var(--color-border-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-ink);
}

.operation-label {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-ink);
}

.operation-meta,
.operation-sync {
  color: var(--color-ink-soft);
  font-size: 0.78rem;
}

.operation-count {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.05em;
  color: var(--color-ink);
}

@media (max-width: 720px) {
  .operations-grid {
    grid-template-columns: 1fr;
  }

  .alert-copy-row {
    align-items: stretch;
    flex-direction: column;
  }
}

/* DASH-02 + DASH-03 tiles (Phase 18) */
.dashboard-tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.9rem;
}

.tile-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 7.5rem;
}

.tile-link {
  color: inherit;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tile-link:hover {
  color: var(--color-primary);
}

.tile-muted {
  color: var(--color-ink-soft);
}

.tile-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.tile-row-link {
  color: inherit;
  text-decoration: none;
  display: inline-flex;
}

.tile-row-link:hover {
  color: var(--color-primary);
}

.tile-empty {
  color: var(--color-ink-soft);
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tile-empty-link {
  font-size: 0.78rem;
  color: var(--color-primary);
  text-decoration: none;
}

.tile-empty-link:hover {
  text-decoration: underline;
}

.tile-failed {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.tile-failed-number {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.05em;
  color: var(--color-ink);
}

.tile-failed-subtitle {
  color: var(--color-ink-soft);
  font-size: 0.78rem;
}

/* Split-button (DASH-01) */
.split-button {
  position: relative;
  display: inline-flex;
  align-items: stretch;
  gap: 0;
}

.split-button :deep(.button) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.split-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.5rem;
  border: 1px solid var(--color-border-strong);
  border-left-width: 0;
  background: var(--color-surface);
  color: var(--color-ink);
  border-top-right-radius: var(--radius-sm);
  border-bottom-right-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s;
}

.split-chevron:hover {
  background: var(--color-surface-muted);
}

.split-chevron:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-surface), 0 0 0 4px var(--color-primary);
}

.split-menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  z-index: 10;
  min-width: 9rem;
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  box-shadow: 0 1px 2px rgba(21, 32, 51, 0.04);
}

.split-menu li {
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--color-ink);
}

.split-menu li:hover {
  background: var(--color-surface-muted);
}

@media (max-width: 1080px) {
  .dashboard-tiles {
    grid-template-columns: 1fr;
  }
}
</style>
